/* eslint-disable react/prop-types -- this codebase does not use propTypes (see CLAUDE.md). */
import { useState } from "react";
import AddressAutocomplete from "../../components/AddressAutocomplete";
import MapPicker from "../../components/MapPicker";
import { useLanguage } from "../../contexts/LanguageContext";
import { ErrorMessages } from "../../components/ui/Modal";
import {
  buttonClass,
  inputClass,
  labelClass,
} from "../../components/ui/styles";

// Add or edit one branch: a name plus a location picked from the address search
// or by dragging the map pin. `branch` is the one being edited, null for a new one.
export default function BranchForm({
  branch,
  onSubmit,
  onCancel,
  isPending,
  errors,
}) {
  const { t } = useLanguage();
  const [name, setName] = useState(branch?.name || "");
  const [location, setLocation] = useState(
    branch
      ? { address: branch.address, lat: branch.latitude, lng: branch.longitude }
      : null,
  );
  const [submitted, setSubmitted] = useState(false);
  // Remounts the Maps-backed inputs when the merchant retries a failed load.
  // AddressAutocomplete owns the retry button; MapPicker hid itself on the same
  // shared promise and has no way to notice a retry on its own.
  const [mapsAttempt, setMapsAttempt] = useState(0);
  const retryMaps = () => setMapsAttempt((n) => n + 1);

  const nameError = !name.trim()
    ? t("settings.branches.form.nameRequired")
    : "";
  const locationError = !location
    ? t("settings.branches.form.addressRequired")
    : "";
  const prefix = branch ? `branch-${branch.id}` : "branch-new";

  const save = () => {
    setSubmitted(true);
    if (nameError || locationError) return;
    onSubmit({
      name: name.trim(),
      address: location.address,
      latitude: location.lat,
      longitude: location.lng,
    });
  };

  return (
    <div className="flex flex-col gap-2.5 rounded-xl border-2 border-indigo-600 bg-indigo-50/40 px-4 py-3.5">
      {!branch && (
        <h3 className="text-sm font-semibold text-gray-900">
          {t("settings.branches.form.newBranch")}
        </h3>
      )}
      <div className="flex flex-col gap-1.5">
        <label className={labelClass}>
          {t("settings.branches.form.name")}
          <input
            type="text"
            name="branch-name"
            autoComplete="off"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t("settings.branches.form.namePlaceholder")}
            aria-invalid={submitted && nameError ? true : undefined}
            aria-describedby={
              submitted && nameError ? `${prefix}-name-error` : undefined
            }
            className={`${inputClass} font-normal`}
          />
        </label>
        {submitted && nameError && (
          <span
            id={`${prefix}-name-error`}
            role="alert"
            className="text-[13px] text-red-700"
          >
            {nameError}
          </span>
        )}
      </div>
      <div className="flex flex-col gap-1.5">
        <span className="text-[13px] font-semibold text-gray-700">
          {t("settings.branches.form.address")}
        </span>
        <AddressAutocomplete
          key={`address-${mapsAttempt}`}
          initialValue={branch?.address}
          onSelectAddress={(next) => next && setLocation(next)}
          onRetry={retryMaps}
        />
        <div className="mt-2">
          <MapPicker
            key={`map-${mapsAttempt}`}
            location={location}
            onSelectLocation={(next) => next && setLocation(next)}
          />
        </div>
        {submitted && locationError && (
          <span role="alert" className="text-[13px] text-red-700">
            {locationError}
          </span>
        )}
      </div>
      <p className="text-xs text-gray-600">
        {t("settings.branches.form.locationHint")}
      </p>
      <ErrorMessages messages={errors} />
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={isPending}
          className={buttonClass.small}
        >
          {t("common.cancel")}
        </button>
        <button
          type="button"
          onClick={save}
          disabled={isPending}
          className="inline-flex min-h-9 items-center justify-center rounded-lg bg-indigo-600 px-3.5 text-[13px] font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
        >
          {isPending ? t("common.saving") : t("common.save")}
        </button>
      </div>
    </div>
  );
}
